import { MenuItem, MENUS } from "../../constants";
import { ROLE } from "../enums";
import { OrganizationModel, UserModel } from "../types";

/**
 * The permission gates that apply to a URL, flattened out of the menu tree.
 *
 * Gates are inherited from ancestors: "Campaign Contact Lists"
 * (`/whatsapp/lists`) sits under the "Campaigns" group even though its URL is
 * not a child of `/whatsapp/campaigns`, so the tree — not the path — decides
 * what applies. Campaigns itself carries no gate any more; Settings > Users is
 * the live example.
 */
interface MenuGate {
  target: string;
  adminOnly: boolean;
  staffOnly: boolean;
  mainOrgOnly: boolean;
  roles: ROLE[];
}

const EMPTY_GATE: Omit<MenuGate, "target"> = {
  adminOnly: false,
  staffOnly: false,
  mainOrgOnly: false,
  roles: [],
};

const collectGates = (
  items: MenuItem[],
  inherited: Omit<MenuGate, "target">,
  out: MenuGate[],
) => {
  items.forEach((item) => {
    const gate = {
      adminOnly: inherited.adminOnly || !!item.admin_only,
      staffOnly: inherited.staffOnly || !!item.staff_only,
      mainOrgOnly: inherited.mainOrgOnly || !!item.only_main_org,
      roles: item.has_role
        ? [...inherited.roles, item.has_role]
        : inherited.roles,
    };

    [item.prefix, item.link].forEach((target) => {
      if (target) {
        out.push({ ...gate, target });
      }
    });

    if (item.subMenus?.length) {
      collectGates(item.subMenus, gate, out);
    }
  });
};

let gateCache: MenuGate[] | null = null;

const getGates = (): MenuGate[] => {
  if (!gateCache) {
    gateCache = [];
    collectGates(MENUS, EMPTY_GATE, gateCache);
  }
  return gateCache;
};

/** Segment-aware prefix match, so `/dash` does not claim `/dashboard`. */
const covers = (pathname: string, target: string) =>
  target !== "/" &&
  (pathname === target || pathname.startsWith(`${target}/`));

const isGateSatisfied = (
  gate: MenuGate,
  organization?: OrganizationModel | null,
  currentUser?: UserModel | null,
) => {
  const roles = organization?.relationship?.roles || [];

  if (gate.adminOnly && !roles.includes(ROLE.ADMIN)) {
    return false;
  }
  if (gate.staffOnly && !currentUser?.is_staff) {
    return false;
  }
  if (gate.mainOrgOnly && organization?.parent_uuid) {
    return false;
  }
  return gate.roles.every((role) => roles.includes(role));
};

/**
 * Whether a single menu item's own gates are satisfied.
 *
 * The sidebar applies this at every level, through `visibleMenuTree`. It used
 * to apply at the top level and to the active item's direct children only, and
 * the filtered copy of those children was never rendered -- see that function.
 * Inherited gates are not considered here; that is what the flattening above
 * is for.
 */
export const isMenuItemAllowed = (
  item: MenuItem,
  organization?: OrganizationModel | null,
  currentUser?: UserModel | null,
): boolean => {
  const roles = organization?.relationship?.roles || [];

  if (item.admin_only && !roles.includes(ROLE.ADMIN)) {
    return false;
  }
  if (item.staff_only && !currentUser?.is_staff) {
    return false;
  }
  if (item.has_role && !roles.includes(item.has_role)) {
    return false;
  }
  return !item.only_main_org || !organization?.parent_uuid;
};

/**
 * The menu tree with everything this user may not see removed, at every level.
 *
 * The sidebar used to filter only the top level and the active item's direct
 * children, but `MenuNav` renders each item's own `subMenus` -- so the filtered
 * copy was computed and thrown away, and every gate below the top level had no
 * effect on what was drawn. Campaigns carried `has_role: campaign` at the time
 * and showed for everyone; clicking it landed on the dashboard, because
 * `OrgScope` applies these same gates to the URL and is not fooled by what the
 * sidebar drew. That gate is gone -- the page answers for itself now -- but the
 * pruning is what stops the next gate from being drawn and then bounced.
 *
 * Pruning rather than flagging: an item whose gate fails takes its children
 * with it, so a group nobody may open cannot leave reachable-looking leaves
 * behind.
 */
export const visibleMenuTree = (
  items: MenuItem[],
  organization?: OrganizationModel | null,
  currentUser?: UserModel | null,
): MenuItem[] =>
  items
    .filter((item) => isMenuItemAllowed(item, organization, currentUser))
    .map((item) =>
      item.subMenus?.length
        ? {
            ...item,
            subMenus: visibleMenuTree(item.subMenus, organization, currentUser),
          }
        : item,
    );

/**
 * Whether `pathname` is reachable for the given organization and user, using
 * the same gates the sidebar applies when it decides what to show.
 *
 * Paths the menu says nothing about are allowed — this guard exists to catch
 * permission downgrades across an organization switch (admin in A, read-only in
 * B), not to act as a second router. Unknown URLs are already handled by the
 * catch-all redirect in `App`.
 */
export const isRouteAllowedForOrg = (
  pathname: string,
  organization?: OrganizationModel | null,
  currentUser?: UserModel | null,
): boolean => {
  const matches = getGates().filter((gate) => covers(pathname, gate.target));

  if (!matches.length) {
    return true;
  }

  // The most specific menu entry wins; if several entries lead to the same URL
  // and any one of them is reachable, so is the page.
  const depth = Math.max(...matches.map((gate) => gate.target.length));
  return matches
    .filter((gate) => gate.target.length === depth)
    .some((gate) => isGateSatisfied(gate, organization, currentUser));
};
